/**
 * 本日の速報(2026-09-20。docs/specs/bluebean-api.md §13)。
 *
 * 1日分をまるごと取り直すと顧客3万件になり BlueBean の負担が大きいので、**前回の続きから今までの分だけ**を取り、
 * 前の値に足していく。日中に何度も呼ぶための作り。
 *
 *  - 取れるのは 取得件数・架電数(オペレーターにつながった通話)・最初と最後の架電時刻 だけ。
 *    実稼働時間・後処理・休憩・離席はオペレーターレポートにしか無く、API が存在しない(ソフツー回答 A-4)
 *  - 取得は「顧客の最新リアクションがアポ〇」で数えるため、同じ顧客が後の窓にもう一度出てきても
 *    二重に数えないよう、数えた顧客の**印(ハッシュ)**を持つ。アポが取り消されたら引く
 *  - **前の日までに数えた顧客も数えない**(台帳 `bb_api_known/{月}`)。APIは1日ぶんしか見ないため、
 *    前後の日にアポになった顧客へその日も架電していると、その日にも1件と数えてしまう(2026-09-20 実測で確認。P-48)
 *  - 顧客の電話番号・氏名は保存しない。印はサーバー側でハッシュにしてから渡す(`functions/src/bluebean.ts`)
 */
import { HOUR_BANDS, type DateStr } from '@core/lib/types';
import type { BbApiCustomer } from './bluebeanApi';
import { callEnd, callStart, isAppointment, isUnconnected } from './bluebeanApiDaily';

/** 速報の値(1人 × 1業務)。by_hour・calls_by_hour は HOUR_BANDS(9〜19時台)と同じ並び */
export type LiveStat = { calls: number; appointments: number; first_at: string | null; last_at: string | null; by_hour: number[]; calls_by_hour: number[] };

/** 窓の中で見えた顧客1件。key は電話番号を含むので、**保存する前に必ずハッシュにする** */
export type LiveSeen = { key: string; appointment: boolean; operator_id: string; operator_name: string; hour: number };

/** 窓(前回の続き〜今)1業務分のまとめ */
export type LiveWindow = {
  /** 内部ID → 架電数・最初と最後の時刻(この窓の分だけ。取得件数は seen から数える) */
  operators: Record<string, { name: string; stat: LiveStat }>;
  seen: LiveSeen[];
  customers: number;
};

/** 保存するときの1件(key をハッシュにしたもの) */
export type LiveHashedSeen = Omit<LiveSeen, 'key'> & { hash: string };

/** 時台 → HOUR_BANDS の位置(枠外は -1) */
const hourIndex = (hms: string): number => (HOUR_BANDS as readonly number[]).indexOf(Number(hms.slice(0, 2)));

/** 業務(会社)1件分の取得結果 */
export type LivePart = { code: string; window: LiveWindow | null; hashed: LiveHashedSeen[]; error: string | null };

/** bb_api_live/{date} */
export type BbApiLive = {
  date: DateStr;
  /** ここまで取り込んだ(JST の 'HH:MM:SS')。画面の「◯時◯分時点」に使う */
  window_to: string;
  /** 業務コード → その業務を取り込んだ終端。次はこの続きから取る(失敗した業務は進めないので取りこぼさない) */
  windows: Record<string, string>;
  updated_at?: unknown;
  /** ok=全部取れた / partial=一部の業務が取れなかった / error=何も取れなかった */
  status: 'ok' | 'partial' | 'error';
  /** 内部ID → 業務コード → 速報値 */
  operators: Record<string, { name: string; by_code: Record<string, LiveStat> }>;
  /** 取得として数えた顧客の印(ハッシュ → その取得の時台の位置)。業務コードごと。古い形(配列)も読める */
  counted: Record<string, Record<string, number> | string[]>;
  campaigns: { code: string; error: string | null }[];
  messages: string[];
};

const emptyLiveStat = (): LiveStat => ({ calls: 0, appointments: 0, first_at: null, last_at: null, by_hour: HOUR_BANDS.map(() => 0), calls_by_hour: HOUR_BANDS.map(() => 0) });
/** 古い形(ハッシュの配列)も新しい形(ハッシュ → 時台)も同じ形で読む */
const countedMap = (v: Record<string, number> | string[] | undefined): Record<string, number> =>
  Array.isArray(v) ? Object.fromEntries(v.map((h) => [h, -1])) : { ...(v ?? {}) };

/**
 * 顧客発着信履歴出力API の返り値(1業務・窓の分)を、オペレーター(内部ID)ごとにまとめる。
 * 取得は「最新リアクションがアポ〇 で、この窓にオペレーターにつながった通話がある顧客」を1件と数える
 * (日をまたいだ判定はしない。確定値は翌日のCSV取込で入れ直す)
 */
export function summarizeLiveWindow(data: BbApiCustomer[], date: DateStr): LiveWindow {
  const operators: LiveWindow['operators'] = {};
  const seen: LiveSeen[] = [];
  let customers = 0;

  for (const cust of data) {
    const today = (cust.calls ?? []).filter((c) => callStart(c) !== '' && callStart(c).slice(0, 10) === date);
    if (today.length === 0) continue;
    customers++;
    const connected: { at: string; end: string; id: string; name: string }[] = [];
    for (const c of today) {
      const id = (c.operator_id ?? '').toString().trim();
      if (isUnconnected(id)) continue;
      const name = (c.operator_name ?? '').toString().trim();
      const hms = callStart(c).slice(11);
      const o = (operators[id] ??= { name, stat: emptyLiveStat() });
      if (!o.name && name) o.name = name;
      o.stat.calls++;
      const ci = hourIndex(hms);
      if (ci >= 0) o.stat.calls_by_hour[ci] = (o.stat.calls_by_hour[ci] ?? 0) + 1;
      if (o.stat.first_at === null || hms < o.stat.first_at) o.stat.first_at = hms;
      if (o.stat.last_at === null || hms > o.stat.last_at) o.stat.last_at = hms;
      connected.push({ at: callStart(c), end: callEnd(c), id, name });
    }
    if (connected.length === 0) continue;
    // 取得は、この窓でつながった最後の通話のオペレーターに付ける
    const last = [...connected].sort((a, b) => a.end.localeCompare(b.end) || a.at.localeCompare(b.at)).pop()!;
    const phone = (today.find((c) => (c.phone ?? '') !== '')?.phone ?? '').toString().replace(/\D/g, '');
    const customerId = (cust.master?.id ?? '').toString();
    const key = phone !== '' ? `tel:${phone}` : customerId !== '' ? `cid:${customerId}` : '';
    if (key === '') continue;
    seen.push({ key, appointment: isAppointment((cust.master?.response ?? '').toString()), operator_id: last.id, operator_name: last.name, hour: hourIndex(last.at.slice(11)) });
  }
  return { operators, seen, customers };
}

/** 前の速報に、今回の窓の分を足す。同じ顧客の取得は二重に数えず、取り消されたら引く */
export function mergeLive(prev: BbApiLive | null, date: DateStr, windowTo: string, parts: LivePart[], messages: string[] = [], known?: Set<string>): BbApiLive {
  const same = prev?.date === date;
  const operators: BbApiLive['operators'] = structuredClone(same ? (prev.operators ?? {}) : {});
  const counted: BbApiLive['counted'] = structuredClone(same ? (prev.counted ?? {}) : {});
  const windows: BbApiLive['windows'] = structuredClone(same ? (prev.windows ?? {}) : {});
  const statOf = (internalId: string, code: string, name: string) => {
    const op = (operators[internalId] ??= { name, by_code: {} });
    if (!op.name && name) op.name = name;
    return (op.by_code[code] ??= emptyLiveStat());
  };

  for (const part of parts) {
    if (!part.window) continue;
    windows[part.code] = windowTo;
    for (const [internalId, o] of Object.entries(part.window.operators)) {
      const stat = statOf(internalId, part.code, o.name);
      stat.calls += o.stat.calls;
      stat.calls_by_hour = stat.calls_by_hour.map((v, i) => v + (o.stat.calls_by_hour[i] ?? 0));
      if (o.stat.first_at !== null && (stat.first_at === null || o.stat.first_at < stat.first_at)) stat.first_at = o.stat.first_at;
      if (o.stat.last_at !== null && (stat.last_at === null || o.stat.last_at > stat.last_at)) stat.last_at = o.stat.last_at;
    }
    const map = countedMap(counted[part.code]);
    for (const s of part.hashed) {
      const has = s.hash in map;
      const stat = statOf(s.operator_id, part.code, s.operator_name);
      // 前の日までに数えた顧客は数えない(日をまたいだ二重計上を防ぐ)
      if (s.appointment && !has && known?.has(s.hash)) continue;
      if (s.appointment && !has) {
        map[s.hash] = s.hour;
        stat.appointments++;
        if (s.hour >= 0) stat.by_hour[s.hour] = (stat.by_hour[s.hour] ?? 0) + 1;
      } else if (!s.appointment && has) {
        // アポが取り消された(リアクションが変わった)
        const hour = map[s.hash] ?? -1;
        delete map[s.hash];
        stat.appointments = Math.max(0, stat.appointments - 1);
        if (hour >= 0) stat.by_hour[hour] = Math.max(0, (stat.by_hour[hour] ?? 0) - 1);
      }
    }
    counted[part.code] = map;
  }

  const failed = parts.filter((p) => p.error).length;
  const status: BbApiLive['status'] = parts.length === 0 || failed === parts.length ? 'error' : failed > 0 ? 'partial' : 'ok';
  return {
    date,
    window_to: windowTo,
    windows,
    status,
    operators,
    counted,
    campaigns: parts.map((p) => ({ code: p.code, error: p.error })),
    messages,
  };
}

/** 速報の合計(その人の BlueBean 内部IDをまとめて渡す)。内部IDが1つも登録されていなければ null */
export function liveTotalsOf(
  live: BbApiLive | null,
  internalIds: string[],
): { appointments: number; calls: number; last_at: string | null; by_hour: number[]; calls_by_hour: number[] } | null {
  if (!live) return null;
  const ids = internalIds.filter((id) => id.trim() !== '');
  if (ids.length === 0) return null;
  let appointments = 0;
  let calls = 0;
  let last: string | null = null;
  const by_hour = HOUR_BANDS.map(() => 0);
  const calls_by_hour = HOUR_BANDS.map(() => 0);
  for (const id of ids) {
    for (const stat of Object.values(live.operators[id]?.by_code ?? {})) {
      appointments += stat.appointments;
      calls += stat.calls;
      if (stat.last_at !== null && (last === null || stat.last_at > last)) last = stat.last_at;
      HOUR_BANDS.forEach((_, i) => {
        by_hour[i] = (by_hour[i] ?? 0) + (stat.by_hour?.[i] ?? 0);
        calls_by_hour[i] = (calls_by_hour[i] ?? 0) + (stat.calls_by_hour?.[i] ?? 0);
      });
    }
  }
  return { appointments, calls, last_at: last, by_hour, calls_by_hour };
}

/** 'HH:MM:SS' の1秒あと(窓の続きを取るときに同じ通話を2回数えないため) */
export function plusOneSecond(hms: string): string {
  const [h, m, s] = hms.split(':').map(Number) as [number, number, number];
  const t = Math.min(h * 3600 + m * 60 + s + 1, 24 * 3600 - 1);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(Math.floor(t / 3600))}:${p(Math.floor((t % 3600) / 60))}:${p(t % 60)}`;
}
